import React, { useState } from 'react'
import { Box, Button, Menu, MenuItem, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { AppBrandMark } from '@components'
import { dsColorNew } from '@palette'
import { fmtDay, fmtWeekday, fromIso } from '../../../@services/dates'
import { money, signedMoney, signedPct } from '../../../@services/format'


const ROW_H = 53
const head = { position: 'sticky', backgroundColor: dsColorNew.tableHead, color: 'rgba(255,255,255,.92)', fontWeight: 700, fontSize: 13, zIndex: 3 }

function Cell({ c, code }) {
	if (!c || c.value === null) return <Box component="td" sx={{ textAlign: 'center', color: dsColorNew.textSubtle, fontSize: 13 }}>—</Box>
	const tint = c.low ? alpha(dsColorNew.successText, 0.1) : c.high ? alpha(dsColorNew.dangerRed, 0.08) : 'transparent'
	const up = c.eur > 0, flat = c.eur !== null && Math.round(c.eur) === 0
	return <Box component="td" sx={{ backgroundColor: tint, px: 1, py: 0.5, minWidth: 92, verticalAlign: 'middle' }}>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end', fontWeight: 600, fontSize: 13.5, color: dsColorNew.textTitle, fontVariantNumeric: 'tabular-nums' }}>
			{c.low && <Box component="span" title="Cheapest of the providers shown" sx={{ fontSize: 10, fontWeight: 700, color: dsColorNew.successText, border: `1px solid ${dsColorNew.successText}`, borderRadius: '4px', px: 0.4, lineHeight: '14px' }}>L</Box>}
			{c.high && <Box component="span" title="Dearest of the providers shown" sx={{ fontSize: 10, fontWeight: 700, color: dsColorNew.dangerRed, border: `1px solid ${dsColorNew.dangerRed}`, borderRadius: '4px', px: 0.4, lineHeight: '14px' }}>H</Box>}
			{money(c.value, code)}
			{c.thin && <Box component="span" title="Fewer than 5 prices" sx={{ color: dsColorNew.noteAmber, fontWeight: 700 }}>!</Box>}
		</Box>
		<Box sx={{ textAlign: 'right', fontSize: 11.5, whiteSpace: 'nowrap', fontWeight: 600, color: c.eur === null ? dsColorNew.textSubtle : flat ? dsColorNew.textMuted : up ? dsColorNew.dangerRed : dsColorNew.successText }}>
			{c.eur === null ? (c.noPrev ? 'no prev. week' : '') : `${flat ? '=' : up ? '▲' : '▼'} ${signedPct(c.pct)} ${signedMoney(c.eur, code)}`}
		</Box>
	</Box>
}

/**
 * "Prices by capture week" (CU-869fmjzrg UX § 8, P1/P5/P6): a grey row per office (name and code), its providers under
 * it — You and Market median on top, the others by name; columns = pick-up dates split into the capture weeks. First
 * column and headers stay when scrolling. `Curve` on a date opens its curve (asks which office when there are several).
 */
export default function WeeklyTable({ table, code, onCurve }) {
	const [menu, setMenu] = useState(null)
	const weeks = table.weeks
	return <Box sx={{ maxHeight: 'calc(100vh - 240px)', overflow: 'auto', border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '12px' }}>
		<Box component="table" sx={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', fontSize: 13, '& td': { borderBottom: `1px solid ${dsColorNew.grayBorder}`, height: ROW_H } }}>
			<thead>
				<tr>
					<Box component="th" rowSpan={2} sx={{ ...head, top: 0, left: 0, zIndex: 5, textAlign: 'left', px: 2, minWidth: 200 }}>Office / Provider</Box>
					{table.pickupDates.map(d => <Box component="th" key={d} colSpan={weeks.length} sx={{ ...head, top: 0, px: 1.5, py: 0.75, borderLeft: '1px solid rgba(255,255,255,.18)' }}>
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
							<span>{fmtWeekday(fromIso(d))}</span>
							<Button size="small" variant="text" onClick={e => (table.offices.length > 1 ? setMenu({ el: e.currentTarget, date: d }) : onCurve(table.offices[0]?.id, d))}
								sx={{ textTransform: 'none', color: '#fff', minWidth: 0, py: 0, px: 0.75, fontSize: 12, border: '1px solid rgba(255,255,255,.4)', borderRadius: '6px' }}>Curve</Button>
						</Box>
					</Box>)}
				</tr>
				<tr>
					{table.pickupDates.map(d => weeks.map((w, i) => <Box component="th" key={`${d}-${w.key}`} sx={{ ...head, top: 38, px: 1, py: 0.5, fontWeight: 600, fontSize: 12, textAlign: 'right', borderLeft: i === 0 ? '1px solid rgba(255,255,255,.18)' : 0, whiteSpace: 'nowrap' }}>
						<div>{w.label}</div>
						<Box sx={{ fontWeight: 400, fontSize: 11, opacity: 0.85 }}>{fmtDay(fromIso(w.from), false)}–{fmtDay(fromIso(w.to), false)}{w.inProgress ? ' · in progress' : ''}</Box>
						<Box sx={{ fontWeight: 400, fontSize: 11, opacity: 0.85 }}>{w.first ? 'no previous week' : w.versus || ''}</Box>
					</Box>))}
				</tr>
			</thead>
			<tbody>
				{table.offices.map(o => <React.Fragment key={o.id}>
					<tr>
						<Box component="td" colSpan={1 + table.pickupDates.length * weeks.length} sx={{ position: 'sticky', left: 0, backgroundColor: dsColorNew.pillTrackBg, px: 2, fontWeight: 700, color: dsColorNew.textTitle, height: '36px !important' }}>
							{o.label} <Box component="span" sx={{ fontWeight: 400, color: dsColorNew.textMuted, ml: 1 }}>{o.empty ? 'No prices captured for this office in these weeks.' : `${o.rows.filter(r => r.kind !== 'median' && r.kind !== 'you').length} providers`}</Box>
						</Box>
					</tr>
					{!o.empty && o.rows.map(r => <Box component="tr" key={`${o.id}-${r.key}`} sx={{ backgroundColor: r.kind === 'you' ? alpha(dsColorNew.appBlue, 0.05) : r.kind === 'median' ? dsColorNew.tableRowAlt : dsColorNew.white }}>
						<Box component="td" sx={{ position: 'sticky', left: 0, zIndex: 2, backgroundColor: 'inherit', px: 2, whiteSpace: 'nowrap', fontWeight: r.kind === 'other' ? 400 : 600, borderRight: `1px solid ${dsColorNew.grayBorder}` }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
								{r.kind !== 'median' && <AppBrandMark name={r.name} size={16}/>}
								{r.name}
								{!!r.tag && <Box component="span" sx={{ px: 0.75, borderRadius: '999px', fontSize: 11, fontWeight: 600, backgroundColor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue }}>{r.tag}</Box>}
								{r.kind === 'median' && <Box component="span" sx={{ fontSize: 11.5, color: dsColorNew.textMuted, fontWeight: 400 }}>{r.competitors} competitor{r.competitors === 1 ? '' : 's'}</Box>}
							</Box>
						</Box>
						{table.pickupDates.map(d => weeks.map(w => <Cell key={`${d}-${w.key}`} c={r.cells[d][w.key]} code={code}/>))}
					</Box>)}
				</React.Fragment>)}
			</tbody>
		</Box>
		<Menu anchorEl={menu?.el} open={!!menu} onClose={() => setMenu(null)}>
			<Typography sx={{ px: 2, py: 0.5, fontSize: 12, color: dsColorNew.textMuted }}>Curve of which office?</Typography>
			{table.offices.map(o => <MenuItem key={o.id} onClick={() => { onCurve(o.id, menu.date); setMenu(null) }}>{o.label}</MenuItem>)}
		</Menu>
	</Box>
}

export function WeeklyLegend({ daily }) {
	return <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', fontSize: 12, color: dsColorNew.textMuted, mt: 1.25 }}>
		<span><b style={{ color: dsColorNew.successText }}>▼</b> cheaper than the week before</span>
		<span><b style={{ color: dsColorNew.dangerRed }}>▲</b> dearer</span>
		<span><b style={{ color: dsColorNew.successText }}>L</b> cheapest</span>
		<span><b style={{ color: dsColorNew.dangerRed }}>H</b> dearest</span>
		<span><b style={{ color: dsColorNew.noteAmber }}>!</b> fewer than 5 prices</span>
		<span>— no price that week</span>
		{daily && <span>Daily = total ÷ rental days</span>}
	</Box>
}
